// 運営バックオフィス(BO)の API 呼び出し。
// 顧客アプリの token ヘッダは付けず(skipAuthHeaders)、admin_session Cookie のみで認証する。
// 401 でも顧客用 /login へ飛ばさない(skipAuthRedirect)。ガードは /admin/me で行う。

import {
  apiDelete,
  apiGet,
  apiPost,
  apiPut,
  type ApiClientOptions,
  type ApiFetchResult,
} from '@/lib/api/client';
import type {
  AdminAuditLog,
  AdminInvitation,
  AdminMe,
  AdminOrganizationDetail,
  AdminOrganizationSummary,
  AdminUser,
} from '@/types/admin';

const ADMIN_OPTS: ApiClientOptions = {
  skipAuthHeaders: true,
  skipAuthRedirect: true,
};

/** ページング結果(総件数を含む) */
export interface AdminPaged<T> {
  items: T[];
  count: number;
}

// 一覧の総件数(count)を使うため、エンベロープ({data,limit,offset,count})を
// そのまま読む生fetch。admin_session Cookie(credentials)のみで認証する。
async function adminGetPaged<T>(endpoint: string): Promise<AdminPaged<T>> {
  try {
    const res = await fetch(`${process.env.NEXT_PUBLIC_API_URL}${endpoint}`, {
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
    });
    if (!res.ok) return { items: [], count: 0 };
    const json = await res.json();
    return { items: (json?.data as T[]) ?? [], count: (json?.count as number) ?? 0 };
  } catch {
    return { items: [], count: 0 };
  }
}

export function fetchAdminMe(): Promise<ApiFetchResult<AdminMe>> {
  return apiGet<AdminMe>('/admin/me', ADMIN_OPTS);
}

// 全組織を取得(組織名マップ等の用途。一覧表示はページング版を使う)
export function fetchOrganizations(): Promise<ApiFetchResult<AdminOrganizationSummary[]>> {
  return apiGet<AdminOrganizationSummary[]>('/admin/organizations?limit=100', ADMIN_OPTS);
}

// 組織一覧(サーバーサイド検索・ページング)
export function fetchOrganizationsPaged(params: {
  q?: string;
  subscriptionStatus?: string;
  limit: number;
  offset: number;
}): Promise<AdminPaged<AdminOrganizationSummary>> {
  const query = new URLSearchParams({
    limit: String(params.limit),
    offset: String(params.offset),
  });
  if (params.q) query.set('q', params.q);
  if (params.subscriptionStatus && params.subscriptionStatus !== 'all') {
    query.set('subscriptionStatus', params.subscriptionStatus);
  }
  return adminGetPaged<AdminOrganizationSummary>(`/admin/organizations?${query.toString()}`);
}

export function fetchOrganization(id: number): Promise<ApiFetchResult<AdminOrganizationDetail>> {
  return apiGet<AdminOrganizationDetail>(`/admin/organizations/${id}`, ADMIN_OPTS);
}

// ユーザー横断検索(サーバーサイド検索・ページング)
export function fetchUsersPaged(params: {
  email?: string;
  organizationId?: number;
  limit: number;
  offset: number;
}): Promise<AdminPaged<AdminUser>> {
  const query = new URLSearchParams({
    limit: String(params.limit),
    offset: String(params.offset),
  });
  if (params.email) query.set('email', params.email);
  if (params.organizationId) query.set('organizationId', String(params.organizationId));
  return adminGetPaged<AdminUser>(`/admin/users?${query.toString()}`);
}

export function fetchPlatformAdmins(): Promise<ApiFetchResult<AdminUser[]>> {
  return apiGet<AdminUser[]>('/admin/platform-admins', ADMIN_OPTS);
}

export function fetchOrganizationInvitations(
  organizationId: number
): Promise<ApiFetchResult<AdminInvitation[]>> {
  return apiGet<AdminInvitation[]>(
    `/admin/organizations/${organizationId}/invitations`,
    ADMIN_OPTS
  );
}

// 組織の監査ログ(サポート操作履歴。総件数を含む)
export function fetchOrganizationAuditLogs(params: {
  organizationId: number;
  limit: number;
  offset: number;
}): Promise<AdminPaged<AdminAuditLog>> {
  const query = new URLSearchParams({
    limit: String(params.limit),
    offset: String(params.offset),
  });
  return adminGetPaged<AdminAuditLog>(
    `/admin/organizations/${params.organizationId}/audit-logs?${query.toString()}`
  );
}

export function fetchAdminGoogleOAuthUrl(): Promise<ApiFetchResult<{ url: string }>> {
  return apiGet<{ url: string }>('/auth/admin/google/oauth-url', ADMIN_OPTS);
}

// ===== サポート変更操作 =====

export function updatePlan(
  organizationId: number,
  body: { plan?: string; clearOverride?: boolean; reason: string }
): Promise<ApiFetchResult<AdminOrganizationDetail>> {
  return apiPut<AdminOrganizationDetail>(
    `/admin/organizations/${organizationId}/plan`,
    body,
    ADMIN_OPTS
  );
}

export function resyncBilling(
  organizationId: number
): Promise<ApiFetchResult<AdminOrganizationDetail>> {
  return apiPost<AdminOrganizationDetail>(
    `/admin/organizations/${organizationId}/billing/resync`,
    undefined,
    ADMIN_OPTS
  );
}

export function setPlatformAdmin(
  userId: number,
  body: { isPlatformAdmin: boolean; reason: string }
): Promise<ApiFetchResult<AdminUser>> {
  return apiPut<AdminUser>(`/admin/users/${userId}/platform-admin`, body, ADMIN_OPTS);
}

export function setUserStatus(
  userId: number,
  body: { isActive: boolean; reason: string }
): Promise<ApiFetchResult<AdminUser>> {
  return apiPut<AdminUser>(`/admin/users/${userId}/status`, body, ADMIN_OPTS);
}

export function sendPasswordReset(userId: number): Promise<ApiFetchResult<{ message: string }>> {
  return apiPost<{ message: string }>(
    `/admin/users/${userId}/password-reset`,
    undefined,
    ADMIN_OPTS
  );
}

export function resendInvitation(
  invitationId: number
): Promise<ApiFetchResult<{ message: string }>> {
  return apiPost<{ message: string }>(
    `/admin/invitations/${invitationId}/resend`,
    undefined,
    ADMIN_OPTS
  );
}

export function cancelInvitation(
  invitationId: number
): Promise<ApiFetchResult<{ message: string }>> {
  return apiDelete<{ message: string }>(`/admin/invitations/${invitationId}`, ADMIN_OPTS);
}
